import type { User } from "firebase/auth";
import { useCallback, useEffect, useRef, useState } from "react";
import * as api from "./api";
import { AreaToggle, useAreaShapes } from "./AreaLayer";
import { memberText } from "./driverRoutes";
import { accountName, dotTitle, levelDot } from "./fill";
import { Hint } from "./Hint";
import { go } from "./hooks";
import { loadMaps, type GoogleMap, type Maps, type Marker, type Polyline, watchMapErrors } from "./maps";
import {
  acceptAll,
  acceptAllText,
  acceptedText,
  driversOf,
  fillText,
  leftOutText,
  madeText,
  mapOpenOf,
  mapTruckOf,
  mapTruckOption,
  POLL_MS,
  statusText,
  stopNumbers,
  stopPinTitle,
  stopShare,
  emptiedText,
  expectedLoad,
  litTruck,
  pinFaded,
  routeLook,
  modeText,
  templateText,
  inTruckOrder,
  summaryText,
  truckColour,
  truckPath,
  truckText,
  whyChip,
  windowText,
  withMapOpen,
  withMapTruck,
} from "./routePlan";
import { MapInfoBox } from "./MapInfoBox";
import { MapFullscreenButton } from "./MapFullscreen";
import { useMarkerCone } from "./ConeCard";
import { OffRouteMark } from "./OffRouteMark";
import { isStraight, lineStyle, routePath, STRAIGHT_TEXT, straightLegend } from "./polyline";
import { saveFailure, SERVER_PROBLEM } from "./save";
import { truckLoads } from "./truckLoad";
import { TruckLoadLine } from "./TruckLoadLine";
import { RouteTimeline } from "./RouteTimeline";
import { timelineOpenOf, withTimelineOpen } from "./routeTimeline";
import { Skeleton } from "./Skeleton";

type Load = api.PlanView | "hidden" | "server" | "network" | undefined;
const DARK = "#0f1216";
const HOVER_PX = 18;
const DEPOT_COLOUR = "#e8eaed";
const POLL_LIMIT = 120;
const TOP_Z = 3000; // a pin under the pointer or the focus, over the other pins

/** The load line of the stop box: "120 gal expected", or the float limit and its reason. */
function boxLoad(stop: api.PlanStop): string | null {
  if (stop.expected_gal === null) return null;
  const load = expectedLoad(stop);
  return load.title ? `${load.text} expected: ${load.title.toLowerCase()}` : `${load.text} expected`;
}
const STARTED = "The planner started. The page shows the plan when it is ready.";
const PLANNED = "Planned";

/** The proposed routes of a day on a Google map: the depots, and the numbered stops of each truck or of one truck. */
function PlanMap({ base, user, view, onFailed }: { base: string; user: User; view: api.PlanView; onFailed: () => void }) {
  const element = useRef<HTMLDivElement>(null);
  const wrap = useRef<HTMLDivElement>(null);
  const map = useRef<GoogleMap | null>(null);
  const drawn = useRef<{ markers: Marker[]; lines: Polyline[] }>({ markers: [], lines: [] });
  const routes = useRef(new Map<string, { line: Polyline; outline: Polyline; markers: Marker[]; colour: string; straight: boolean }>());
  const opener = useRef<Marker | null>(null);
  const top = useRef<Marker | null>(null);
  const zBase = useRef(new Map<Marker, number>());
  const [maps, setMaps] = useState<Maps | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "failed">("loading");
  const [areasOn, setAreasOn] = useState(true);
  const trucks = view.plan?.trucks ?? [];
  const [only, setOnlyState] = useState(() => mapTruckOf(location.search, trucks));
  const [open, setOpen] = useState<{ truck: string; seq: number } | null>(null);
  const cone = useMarkerCone();
  const [hover, setHover] = useState<string | null>(null);
  const shownTruck = trucks.some((truck) => truck.truck === only) ? only : "";
  const setOnly = (next: string) => {
    setOnlyState(next);
    setOpen((was) => (was && next && was.truck !== next ? null : was));
    history.replaceState(history.state, "", `${location.pathname}${withMapTruck(location.search, next)}${location.hash}`);
  };
  useEffect(() => {
    let live = true;
    user
      .getIdToken()
      .then(loadMaps)
      .then((loaded) => {
        if (!live) return;
        setMaps(loaded);
        setState("ready");
      })
      .catch(() => live && setState("failed"));
    return () => {
      live = false;
    };
  }, [user]);
  useEffect(() => {
    if (!maps || !element.current) return;
    const shown =
      map.current ??
      new maps.maps.Map(element.current, {
        center: { lat: 39.8, lng: -98.6 },
        zoom: 4,
        mapId: maps.mapId,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        clickableIcons: false,
      });
    map.current = shown;
    for (const marker of drawn.current.markers) marker.map = null;
    for (const line of drawn.current.lines) line.setMap(null);
    const markers: Marker[] = [];
    const lines: Polyline[] = [];
    routes.current = new Map();
    setHover(null);
    const bounds = new maps.maps.LatLngBounds();
    const placed = view.depots.filter((d) => d.lat !== null && d.lng !== null);
    const depots = new Map(placed.map((d) => [d.id, { lat: d.lat as number, lng: d.lng as number, name: d.name }] as const));
    const one = shownTruck ? (view.plan?.trucks ?? []).find((truck) => truck.truck === shownTruck) : undefined;
    for (const [id, depot] of depots) {
      if (one && one.depot_id !== id) continue;
      const pin = new maps.marker.PinElement({ glyph: "D", background: DEPOT_COLOUR, borderColor: DARK, glyphColor: DARK });
      markers.push(new maps.marker.AdvancedMarkerElement({ map: shown, position: depot, title: depot.name, content: pin.element, zIndex: 2000 }));
      bounds.extend(depot);
    }
    (view.plan?.trucks ?? []).forEach((truck, index) => {
      if (one && truck !== one) return;
      const colour = truckColour(index);
      const depot = truck.depot_id ? (depots.get(truck.depot_id) ?? null) : null;
      const numbers = stopNumbers(truck.stops);
      const own: Marker[] = [];
      const lift = () => setHover(truck.truck);
      const drop = () => setHover((was) => (was === truck.truck ? null : was));
      truck.stops.forEach((stop, at) => {
        if (stop.kind !== "stop" || stop.lat === null || stop.lng === null) return;
        const point = { lat: stop.lat, lng: stop.lng };
        const content = levelDot({ share: stopShare(stop), number: numbers[at] });
        const title = dotTitle([stopPinTitle(truck.truck, numbers[at], stop)], stopShare(stop), stop.float_now);
        const marker = new maps.marker.AdvancedMarkerElement({ map: shown, position: point, title, content, zIndex: 1000 + at, gmpClickable: true });
        cone.watch(content, stop.account_id, stopPinTitle(truck.truck, numbers[at], stop), { words: stop.float_now ? fillText(stop) : undefined, visit: { date: view.day, label: PLANNED } });
        marker.addListener("click", () => {
          opener.current = marker;
          setOpen({ truck: truck.truck, seq: stop.seq });
        });
        const node = marker as unknown as HTMLElement;
        const raise = () => {
          top.current = marker;
          marker.zIndex = TOP_Z;
          lift();
        };
        const lower = () => {
          if (top.current === marker) top.current = null;
          marker.zIndex = zBase.current.get(marker) ?? 1000 + at;
          drop();
        };
        node.addEventListener("mouseenter", raise);
        node.addEventListener("mouseleave", lower);
        node.addEventListener("focusin", raise);
        node.addEventListener("focusout", lower);
        markers.push(marker);
        own.push(marker);
        bounds.extend(point);
      });
      const path = routePath(truck.shape, truckPath(truck, depot));
      const straight = isStraight(truck.shape);
      const outline = new maps.maps.Polyline({ strokeColor: DARK, strokeOpacity: 0, strokeWeight: 8, clickable: false, zIndex: 9 });
      const line = new maps.maps.Polyline({ ...lineStyle(straight, colour, 0.9, 3), clickable: true, zIndex: 10 });
      // An almost clear wide line under the route catches the hover a few pixels from it.
      const hit = new maps.maps.Polyline({ strokeColor: DARK, strokeOpacity: 0.01, strokeWeight: HOVER_PX, clickable: true, zIndex: 8 });
      outline.setPath(path);
      line.setPath(path);
      hit.setPath(path);
      outline.setMap(shown);
      line.setMap(shown);
      hit.setMap(shown);
      const canvas = element.current;
      for (const target of [line, hit]) {
        target.addListener("mouseover", () => {
          if (straight) canvas?.setAttribute("title", `Truck ${truck.truck} · ${STRAIGHT_TEXT}`);
          lift();
        });
        target.addListener("mouseout", () => {
          canvas?.removeAttribute("title");
          drop();
        });
      }
      lines.push(hit, outline, line);
      routes.current.set(truck.truck, { line, outline, markers: own, colour, straight });
    });
    drawn.current = { markers, lines };
    if (markers.length) shown.fitBounds(bounds, 48);
    console.info(`plan map: ${markers.length} pins and ${lines.length} lines for ${shownTruck ? `truck ${shownTruck}` : "all trucks"}`);
  }, [maps, view, shownTruck]);
  const lit = litTruck(hover, open?.truck ?? null, shownTruck);
  const straightNote = straightLegend(trucks.filter((truck) => (!shownTruck || truck.truck === shownTruck) && isStraight(truck.shape)).map((truck) => truck.truck));
  useEffect(() => {
    for (const [truck, route] of routes.current) {
      const look = routeLook(lit, truck);
      route.outline.setOptions({ strokeOpacity: look.outline ? 0.85 : 0, zIndex: look.zIndex - 1 });
      route.line.setOptions({ ...lineStyle(route.straight, route.colour, look.opacity, look.outline ? 4 : 3), zIndex: look.zIndex });
      route.markers.forEach((marker, at) => {
        zBase.current.set(marker, (look.outline ? 1500 : 1000) + at);
        marker.zIndex = marker === top.current ? TOP_Z : (look.outline ? 1500 : 1000) + at;
        (marker as unknown as HTMLElement).style.opacity = pinFaded(lit, truck) ? "0.55" : "";
      });
    }
  }, [lit, maps, view, shownTruck]);
  const { count: areaCount } = useAreaShapes(maps, map, user, areasOn);
  useEffect(() => {
    const target = element.current;
    if (state !== "ready" || !target) return;
    return watchMapErrors(target, () => setState("failed"));
  }, [state]);
  useEffect(() => {
    if (state === "failed") onFailed();
  }, [state, onFailed]);
  useEffect(
    () => () => {
      for (const marker of drawn.current.markers) marker.map = null;
      for (const line of drawn.current.lines) line.setMap(null);
    },
    [],
  );
  const openTruck = open ? trucks.find((truck) => truck.truck === open.truck) : undefined;
  const openAt = openTruck && open ? openTruck.stops.findIndex((stop) => stop.seq === open.seq) : -1;
  const openStop = openTruck && openAt >= 0 ? openTruck.stops[openAt] : undefined;
  const openLoad = openTruck && openAt >= 0 ? truckLoads(openTruck)[openAt] : null;
  const close = () => {
    setOpen(null);
    (opener.current as unknown as HTMLElement | null)?.focus?.();
  };
  return (
    <>
      {(areaCount > 0 || trucks.length > 1) && (
        <div className="pin-map-bar">
          {trucks.length > 1 && (
            <label>
              Truck on the map{" "}
              <select data-testid="plan-map-truck" value={shownTruck} onChange={(e) => setOnly(e.target.value)}>
                <option value="">All trucks</option>
                {trucks.map((truck) => (
                  <option key={truck.truck} value={truck.truck}>
                    {mapTruckOption(truck)}
                  </option>
                ))}
              </select>
            </label>
          )}
          {areaCount > 0 && <AreaToggle on={areasOn} onChange={setAreasOn} />}
        </div>
      )}
      <div ref={wrap} className="pin-map-wrap plan-map-wrap">
        <div ref={element} className="pin-map-canvas plan-map" data-testid="plan-map" data-state={state} data-truck={shownTruck} data-lit={lit ?? ""} role="region" aria-label={shownTruck ? `Map of the proposed route of truck ${shownTruck}` : "Map of the proposed routes"} />
        {state === "ready" && <MapFullscreenButton wrap={wrap} testId="plan-map-fullscreen" />}
        {state === "loading" && <Skeleton shape="map" overlay label="Loading the map" />}
        {state !== "failed" && straightNote && (
          <p className="sub location-map-note map-straight-note" data-testid="plan-map-straight">
            <span className="map-dash" aria-hidden="true" /> {straightNote}
          </p>
        )}
        {state === "failed" && (
          <p className="location-map-note map-warning" id="plan-map-failed" data-testid="plan-map-failed">
            The map cannot load. The lists below show the routes.
          </p>
        )}
        {cone.card}
        {open && openTruck && openStop && (
          <MapInfoBox
            testId="plan-stop-box"
            onClose={close}
            info={{
              id: `${openTruck.truck}-${openStop.seq}`,
              accountId: openStop.account_id,
              title: `Stop ${stopNumbers(openTruck.stops)[openAt]} · Truck ${openTruck.truck}`,
              name: accountName(openStop.number, openStop.name),
              share: stopShare(openStop),
              fillText: openStop.float_now ? fillText(openStop) : undefined,
              visit: { date: view.day, label: PLANNED },
              lines: [`Arrive ${openStop.arrive}`, emptiedText(openStop) ?? boxLoad(openStop), windowText(openStop) && `Window ${windowText(openStop)}`],
              extra: openLoad && openTruck.capacity ? <TruckLoadLine load={openLoad} capacity={openTruck.capacity} /> : null,
              href: openStop.account_id ? `${base}/accounts/${openStop.account_id}` : null,
              data: { truck: openTruck.truck, number: openStop.number ?? "" },
            }}
          />
        )}
      </div>
    </>
  );
}

function WhyChip({ stop, earlyRisk, minVisitDays }: { stop: api.PlanStop; earlyRisk: number; minVisitDays?: number }) {
  const chip = whyChip(stop, earlyRisk, minVisitDays);
  return (
    chip && (
      <Hint text={chip.help} testId="plan-why-hint">
        <span className={chip.serious ? "chip-flag serious" : "chip-flag neutral"}>{chip.text}</span>
      </Hint>
    )
  );
}

export function RoutePlan({
  base,
  user,
  found,
  timeZone,
  onRoutes,
}: {
  base: string;
  user: User;
  found: api.RouteDay;
  timeZone?: string;
  onRoutes: (next: api.RouteDay) => void;
}) {
  const [view, setView] = useState<Load>(undefined);
  const [attempt, setAttempt] = useState(0);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  const [chosen, setChosen] = useState<Record<string, string>>({});
  const [showMap, setShowMapState] = useState(() => mapOpenOf(location.search));
  const setShowMap = (next: boolean) => {
    setShowMapState(next);
    history.replaceState(history.state, "", `${location.pathname}${withMapOpen(location.search, next)}${location.hash}`);
  };
  const [showTimeline, setShowTimelineState] = useState(() => timelineOpenOf(location.search));
  const setShowTimeline = (next: boolean) => {
    setShowTimelineState(next);
    history.replaceState(history.state, "", `${location.pathname}${withTimelineOpen(location.search, next)}${location.hash}`);
  };
  const [mapFailed, setMapFailed] = useState(false);
  const failMap = useCallback(() => setMapFailed(true), []);
  const polls = useRef(0);
  const day = found.day;
  useEffect(() => {
    let live = true;
    user
      .getIdToken()
      .then((token) => api.routePlan(token, day))
      .then((next) => live && setView(inTruckOrder(next)))
      .catch((e) => {
        const code = e instanceof api.ApiError ? e.status : 0;
        if (live) setView(code === 404 ? "hidden" : code >= 500 ? "server" : "network");
      });
    return () => {
      live = false;
    };
  }, [user, day, attempt]);
  const shown = typeof view === "object" ? view : undefined;
  const running = shown?.plan?.status === "running";
  const planStatus = shown?.plan?.status;
  useEffect(() => {
    if (running || !planStatus) return;
    setNote((old) => (old?.text === STARTED ? (planStatus === "failed" ? null : { ok: true, text: "The plan is ready." }) : old));
  }, [running, planStatus]);
  useEffect(() => {
    if (!running) {
      polls.current = 0;
      return;
    }
    if (polls.current >= POLL_LIMIT) return;
    const timer = window.setTimeout(() => {
      polls.current += 1;
      setAttempt((n) => n + 1);
    }, POLL_MS);
    return () => window.clearTimeout(timer);
  }, [running, view]);
  if (view === "hidden") return null;
  if (view === undefined) return <div data-testid="plan-loading"><Skeleton shape="map" label="Loading the proposed routes" /></div>;
  if (typeof view === "string") {
    return (
      <p role="alert" className="error" data-testid="plan-failed-load">
        {view === "server" ? SERVER_PROBLEM : "Cannot reach the server. Check your connection."}{" "}
        <button type="button" onClick={() => setAttempt((n) => n + 1)}>
          Try again
        </button>
      </p>
    );
  }
  const plan = view.plan;
  const trucks = plan?.trucks ?? [];
  const canAccept = found.can_plan && view.can_plan && (plan?.status === "ready" || plan?.status === "accepted");
  const drivers = driversOf(trucks, chosen, found.drivers);
  const everyTruck = acceptAll(trucks, drivers);
  const depotName = (id: string | null) => view.depots.find((depot) => depot.id === id)?.name ?? null;
  const act = async (thing: string, action: (token: string) => Promise<void>, done: string) => {
    if (busy) return;
    setBusy(true);
    setNote(null);
    try {
      await action(await user.getIdToken());
      setNote({ ok: true, text: done });
    } catch (e) {
      setNote({ ok: false, text: saveFailure(e, thing).text });
    } finally {
      setBusy(false);
    }
  };
  const planAgain = () =>
    act(
      "The plan request",
      async (token) => setView(inTruckOrder(await api.planRoutes(token, crypto.randomUUID(), day))),
      STARTED,
    );
  const accept = (list: { truck: string; driver_id: string }[]) =>
    act(
      "The routes",
      async (token) => {
        const next = await api.acceptPlan(token, plan?.id ?? "", list);
        setView(inTruckOrder(next.plan));
        onRoutes(next.routes);
      },
      acceptedText(list.map((entry) => entry.truck)),
    );
  const leftOut = plan && plan.status !== "running" ? leftOutText(plan.summary) : null;
  return (
    <section className="plan-section" aria-labelledby="plan-title" data-testid="route-plan" data-status={plan?.status ?? "none"}>
      <h2 id="plan-title">Proposed routes</h2>
      {plan ? (
        <>
          <p data-testid="plan-status" className={plan.status === "failed" ? "error" : undefined}>
            {statusText(plan)}
          </p>
          <p className="sub" data-testid="plan-made">
            {madeText(plan, timeZone)}
          </p>
          {plan.status !== "running" && plan.status !== "failed" && (
            <p className="sub" data-testid="plan-summary">
              {summaryText(plan.summary)}
            </p>
          )}
          {plan.status !== "running" && plan.status !== "failed" && modeText(plan.summary) && (
            <p className="sub" data-testid="plan-mode">
              {modeText(plan.summary)}
            </p>
          )}
          {leftOut && (
            <p className="sub" data-testid="plan-left-out">
              {leftOut}
            </p>
          )}
        </>
      ) : (
        <p data-testid="plan-none">{view.workday ? "No plan for this day yet. The planner plans the next workday each night." : "No plan on a weekend day or a holiday."}</p>
      )}
      <p role="status" className={note && !note.ok ? "error" : "sub"} data-testid="plan-note">
        {note?.text}
      </p>
      <div className="actions plan-actions">
        {view.can_plan && (
          <button type="button" data-testid="plan-again" disabled={busy || running} onClick={() => void planAgain()}>
            {running ? "Planning…" : plan ? "Plan again" : "Plan this day"}
          </button>
        )}
        {canAccept && trucks.some((truck) => !truck.accepted) && (
          <button type="button" className="primary" data-testid="plan-accept-all" disabled={busy || everyTruck === null} onClick={() => everyTruck && void accept(everyTruck)}>
            {acceptAllText(trucks.filter((truck) => !truck.accepted).length)}
          </button>
        )}
        {trucks.length > 0 && (
          <button
            type="button"
            aria-pressed={showMap && !mapFailed}
            aria-describedby={mapFailed ? "plan-map-failed" : undefined}
            data-testid="plan-map-toggle"
            disabled={mapFailed}
            onClick={() => setShowMap(!showMap)}
          >
            {showMap && !mapFailed ? "Hide the map" : "Show on the map"}
          </button>
        )}
        {trucks.length > 0 && plan?.status !== "running" && (
          <button type="button" aria-pressed={showTimeline} data-testid="plan-timeline-toggle" onClick={() => setShowTimeline(!showTimeline)}>
            {showTimeline ? "Hide the timeline" : "Show the timeline"}
          </button>
        )}
      </div>
      {canAccept && everyTruck === null && trucks.some((truck) => !truck.accepted) && <p className="sub">Choose a driver for each truck to accept all.</p>}
      {showTimeline && trucks.length > 0 && <RouteTimeline base={base} view={view} drivers={drivers} people={found.drivers} timeZone={timeZone} />}
      {showMap && trucks.length > 0 && <PlanMap base={base} user={user} view={view} onFailed={failMap} />}
      {trucks.map((truck, index) => {
        const numbers = stopNumbers(truck.stops);
        const driver = drivers[truck.truck] ?? "";
        return (
          <section key={truck.truck} className="route-card plan-truck" aria-labelledby={`plan-${truck.truck}`} data-testid="plan-truck" data-truck={truck.truck} data-accepted={truck.accepted}>
            <h3 id={`plan-${truck.truck}`}>
              <span className="route-stop-number" style={{ background: truckColour(index) }} aria-hidden="true">
                {truck.truck}
              </span>{" "}
              Truck {truck.truck}
              {truck.accepted && (
                <>
                  {" "}
                  <span className="chip-flag">Accepted</span>
                </>
              )}
            </h3>
            <p className="sub" data-testid="plan-truck-totals">
              {truckText(truck, depotName(truck.depot_id))}
            </p>
            {templateText(truck) && (
              <p className="sub" data-testid="plan-truck-template">
                {templateText(truck)}
              </p>
            )}
            {canAccept && !truck.accepted && (
              <div className="route-tools">
                <label>
                  Driver
                  <select
                    className="plan-driver"
                    data-testid="plan-driver"
                    value={driver}
                    aria-label={`Driver of truck ${truck.truck}`}
                    onChange={(e) => setChosen((all) => ({ ...all, [truck.truck]: e.target.value }))}
                  >
                    <option value="">Choose…</option>
                    {found.drivers.map((person) => (
                      <option key={person.id} value={person.id}>
                        {memberText(person)}
                      </option>
                    ))}
                  </select>
                </label>
                <button type="button" data-testid="plan-accept" disabled={busy || !driver} onClick={() => void accept([{ truck: truck.truck, driver_id: driver }])}>
                  Accept truck {truck.truck}
                </button>
              </div>
            )}
            <div className="table-scroll" tabIndex={0} role="region" aria-label={`Proposed stops of truck ${truck.truck}`}>
              <table className="list route-stops plan-stops" data-testid="plan-stops">
                <thead>
                  <tr>
                    <th scope="col" className="num">
                      Order
                    </th>
                    <th scope="col">Arrive</th>
                    <th scope="col">Account</th>
                    <th scope="col">Area</th>
                    <th scope="col" className="num">
                      Expected load
                    </th>
                    <th scope="col" className="num">
                      Fill (estimated)
                    </th>
                    <th scope="col">Why</th>
                  </tr>
                </thead>
                <tbody>
                  {truck.stops.map((stop, at) =>
                    stop.kind === "dump" ? (
                      <tr key={stop.seq} data-testid="plan-dump" className="plan-dump">
                        <td className="num" data-label="Order">
                          –
                        </td>
                        <td data-label="Arrive">{stop.arrive}</td>
                        <th scope="row" colSpan={5}>
                          Dump at the depot
                        </th>
                      </tr>
                    ) : (
                      <tr key={stop.seq} data-testid="plan-stop" data-number={stop.number ?? ""}>
                        <td className="num" data-label="Order">
                          {numbers[at]}
                        </td>
                        <td data-label="Arrive">{stop.arrive}</td>
                        <th scope="row">
                          <a href={`${base}/accounts/${stop.account_id}`} onClick={go} title={stop.address ?? undefined}>
                            <span className="account-number">{stop.number}</span> {stop.name}
                          </a>
                          {stop.address && <span className="sub desk-hide">{stop.address}</span>}
                        </th>
                        <td data-label="Area">
                          {stop.area ?? "–"}
                          {stop.off_route && <> <OffRouteMark truck={truck.truck} /></>}
                        </td>
                        <td className="num" data-label="Expected load" title={expectedLoad(stop).title ?? undefined}>
                          {expectedLoad(stop).text}
                        </td>
                        <td className="num" data-label="Fill (estimated)">
                          {fillText(stop)}
                        </td>
                        <td className="flags" data-label="Why">
                          <WhyChip stop={stop} earlyRisk={view.early_risk ?? 0.1} minVisitDays={plan?.summary.min_visit_days} />
                        </td>
                      </tr>
                    ),
                  )}
                </tbody>
              </table>
            </div>
          </section>
        );
      })}
    </section>
  );
}
